import { FC, useEffect, useMemo, useState } from 'react';

import { DownloadIcon, XIcon } from '@heroicons/react/outline';
import {
  getCouponPendingSummary,
  getCouponRefundPendingRows,
  getCouponSendPendingRows,
  GiftCouponSettlementRole,
  patchGiftCouponSettlement,
  settleGiftCoupon,
  settleGiftCouponByFilter,
} from '@shared/api/couponSettlement/couponSettlement.controller';
import {
  CouponPendingBrandSummary,
  CouponSettleSkipReport,
  GiftCouponSettlement,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import { useToast } from '../../hooks/useToast';
import {
  amountColorClass,
  amountLabel,
  couponCodeLabel,
  describeSkipped,
  formatSettlementAmount,
  matchPendingRow,
  MAX_ENUM_IDS,
  ymdRangeToIso,
} from '../../utils/coupon-settlement.utils';
import { exportCouponPendingExcel } from '../../utils/couponSettlementExcel';
import { Toast } from '../Toast';
import { CouponPendingSummaryTable, viaMariBadge } from './CouponPendingSummaryTable';
import { CouponRefundPendingTable } from './CouponRefundPendingTable';
import { CouponSearchInput } from './CouponSearchInput';
import { CouponSendPendingTable } from './CouponSendPendingTable';
import { CouponSettleConfirmModal, ExcludedPendingRow } from './CouponSettleConfirmModal';
import { DateFilter, ymdToKr } from './DateFilter';
import { PaginationFooter } from './PaginationFooter';

// ─────────────────────────────────────────────
// CouponPendingSection
//
// 쿠폰형 정산의 "미정산 예정" 화면 — 단계별 로드(통계 → 상세).
// 기본은 Admin(집행 포함)이고, 상조관리자 페이지는 readOnly + role 프롭으로 같은
// 화면을 view 전용으로 쓴다(2026-07-02 사용자 결정 — 초과 배너·뱃지·재발행 뱃지·
// 엑셀까지 admin과 동일 표시, 체크박스·집행 UI만 제거. 스코프는 서버 owningFilter).
// - 페이지에는 pending/summary(상조별 발송/환불 분리 집계) 통계 테이블만 그린다.
//   raw는 상조 상세보기를 눌렀을 때만 export API로 그 상조 분만 불러 **모달**로 띄운다
//   (테이블이 페이지에 길게 펼쳐지지 않게 — 검색·선택·집행 전부 모달 안에서).
// - 모달 검색: 쿠폰번호·수령자·구매자명·연락처(수령자/구매자/지도사)·상품·고인 통합 검색.
//   검색은 표시 필터일 뿐, 포함/제외·집행 대상은 항상 전체 로드 목록 기준이다.
//   (구매자명·구매자/수령자 연락처는 export 행 필드 추가를 백엔드에 요청한 상태 —
//   필드가 오면 별도 배선 없이 검색에 바로 걸린다)
// - 정산서는 "한 상조의 한 달치"라 집행은 모달(단일 상조) 안에서만 일어난다.
//   상조를 바꾸면 제외 선택은 초기화한다.
// - 선택은 "제외" 방식: 기본 전체 포함이고 체크 해제한 건만 빠진다.
//   제외는 쿠폰 단위 — 발송 후 환불된 쿠폰(양쪽 테이블에 존재)은 한쪽만 빼면 상쇄가
//   깨지므로 서버(by-filter)가 양쪽에서 함께 뺀다. 프론트도 단일 Set으로 동기화.
// - 집행은 조건 기반(settle/by-filter: 상조+기간+제외 id, 스펙 docs §5-1)이 기본 —
//   ID 전체 열거는 5,000건 ≈ 135KB로 body limit(100KB)를 넘는다. 포함이 제외보다
//   적으면(소수만 골라 집행) ID 열거 settle이 더 작아 그쪽을 쓴다.
// - 집행 확인 모달에서 추가 조작: 발송/환불 축 토글(수익금만·환불만 집행)과 제외 복원.
//   축을 끈 집행도 by-filter의 includeSend/includeRefund 축 플래그(§5-1 확장,
//   백엔드 반영 완료)로 조건 기반으로 나간다 — 건수 한도 없음. 플래그 미지원
//   구서버는 expected*(끈 축 0건) 대조가 어긋나 집행을 거부하므로 오집행은 없다.
//   기간 변경은 재조회가 필요해 페이지 필터에서만.
// - 모달 하단 바가 포함분 순액(Σ발송 − Σ환불, 음수 가능 = 회수)을 보여준다.
//   행 금액(amount)은 서버가 건별 반올림한 정수라 추가 반올림 없이 합만 낸다.
//   by-filter의 expected*는 반드시 이 export 검토 결과(overQuota·체크 해제 제외 후) 기준
//   (스펙 §5-1) — summary의 netAmount는 overQuota 포함 순수 집계라 초과가 있으면 다르다.
// ─────────────────────────────────────────────

export interface CouponPendingSectionProps {
  /** 집행 성공 후 상위 완료 내역 refetch 트리거 (readOnly면 불필요) */
  onSettled?: () => void;
  /** 외부 변경(정산 라인 편집 등)으로 미정산이 바뀌었을 때 재조회 트리거 (증가값) */
  refreshTrigger?: number;
  /** view 전용 — 체크박스·집행 UI 제거 (상조관리자 페이지). 표시 내용은 admin과 동일 */
  readOnly?: boolean;
  /** API role prefix — 기본 Admin, 상조 페이지는 BugoAgencyAdmin (서버가 본인 상조로 스코프) */
  role?: GiftCouponSettlementRole;
}

const PAGE_SIZE = 20;

type PendingTab = 'send' | 'refund';

/**
 * 서버 중복 에러 메시지에서 기존 정산서 id 추출 (…이미 존재합니다 (id=…)).
 * 이 id로 곧장 병합 PATCH가 나가므로, 다른 에러에 우연히 24-hex id가 섞여도
 * 오동작하지 않게 중복 문구('이미 존재')가 있을 때만 해석한다.
 */
const extractExistingId = (message: string): string | undefined =>
  message.includes('이미 존재') ? message.match(/id=([a-f0-9]{24})/i)?.[1] : undefined;

export const CouponPendingSection: FC<CouponPendingSectionProps> = ({
  onSettled,
  refreshTrigger = 0,
  readOnly = false,
  role = LowerRole.Admin,
}) => {
  const toast = useToast();

  // 1단계 — 미정산 요약 (상조별 집계). 페이지 진입·기간 조회 시에만 호출.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // 마지막 '조회'로 실제 적용된 기간 — 요약·상세 행·집행이 전부 이 기간을 쓴다.
  // date input만 바꾸고 조회를 안 누른 상태에서 상세보기/집행을 해도, 화면(요약 헤더)과
  // 로드된 행·집행 대상 기간이 어긋나지 않게 하는 단일 기준값.
  const [appliedRange, setAppliedRange] = useState({ from: '', to: '' });
  const [{ loading: summaryLoading, value: summary }, fetchSummary] = useAsyncFn(
    (from: string, to: string) => getCouponPendingSummary(ymdRangeToIso(from, to), role),
    [role],
  );

  // 2단계 — 선택한 상조의 raw 행 (export). 상세보기 모달을 열 때만 호출.
  // 상조 role은 서버 스코프라 bugoBrand 파라미터가 무시돼도 무방 (본인 상조 = 선택 상조)
  const [{ loading: sendLoading, value: sendRows }, fetchSendRows] = useAsyncFn(
    (bugoBrand: string, from: string, to: string) =>
      getCouponSendPendingRows({ bugoBrand, ...ymdRangeToIso(from, to) }, role),
    [role],
  );
  const [{ loading: refundLoading, value: refundRows }, fetchRefundRows] = useAsyncFn(
    (bugoBrand: string, from: string, to: string) =>
      getCouponRefundPendingRows({ bugoBrand, ...ymdRangeToIso(from, to) }, role),
    [role],
  );

  const [selectedBrand, setSelectedBrand] = useState(''); // '' = 모달 닫힘(통계만)
  const [tab, setTab] = useState<PendingTab>('send');
  // 모달 통합 검색어 — 표시 필터 전용 (포함/제외·집행 대상은 전체 목록 기준)
  const [query, setQuery] = useState('');
  // 초과만 보기 (경고 배너 토글) — 표시 필터 전용, 발송 테이블에만 의미 있음
  const [showOverQuotaOnly, setShowOverQuotaOnly] = useState(false);
  // 제외 선택(쿠폰 id 단위) — 기본 전체 포함이고, 체크 해제한 쿠폰만 쌓인다.
  // by-filter의 excludeCouponIds가 발송·환불 양쪽에서 빼는 규칙이라 테이블 구분 없이 단일 Set.
  const [excl, setExcl] = useState<Set<string>>(new Set());
  const [sendPage, setSendPage] = useState(1);
  const [refundPage, setRefundPage] = useState(1);

  const resetSelection = () => {
    setExcl(new Set());
    setSendPage(1);
    setRefundPage(1);
  };

  /** 기간 조회 — 기간 확정(appliedRange) 후 상세 모달 닫고 요약 재조회 */
  const reload = (from: string, to: string) => {
    resetSelection();
    setSelectedBrand('');
    setQuery('');
    setAppliedRange({ from, to });
    fetchSummary(from, to);
  };
  useEffect(() => {
    reload('', '');
    // 최초 마운트 1회 로드
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 정산서 라인 편집(제외→미정산 복귀, 추가→미정산 이탈) 등 외부 변경 반영.
    // 0은 마운트 초깃값 — 최초 로드는 위 효과가 담당하므로 건너뛴다.
    // 기간은 조회 확정값 유지 — 입력만 해두고 조회 안 누른 날짜가 몰래 적용되지 않게.
    if (refreshTrigger > 0) reload(appliedRange.from, appliedRange.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  /** 기간 일괄 변경(월별 선택·이번달·지난달·초기화) — 값 반영과 동시에 재조회 */
  const handleRange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    reload(from, to);
  };

  /**
   * 체크 토글 = 제외 목록에 넣고 빼기 (체크됨 = 포함).
   * 쿠폰 단위라 발송·환불 양쪽에 있는 쿠폰은 어느 테이블에서 토글해도 함께 움직인다.
   */
  const toggle = (id: string) => {
    setExcl((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** 상조 상세보기 — 모달 열고 그 상조의 raw 행만 로드 (기간 = 조회 확정 기간) */
  const openDetail = (id: string) => {
    setSelectedBrand(id);
    resetSelection(); // 상조를 바꾸면 제외 선택 초기화 (교차 상조 집행 방지)
    setQuery('');
    setShowOverQuotaOnly(false);
    setTab('send');
    fetchSendRows(id, appliedRange.from, appliedRange.to);
    fetchRefundRows(id, appliedRange.from, appliedRange.to);
  };

  /** 상세 모달 닫기 — 선택·검색 초기화 (집행 없이 닫으면 아무 영향 없음) */
  const closeDetail = () => {
    setSelectedBrand('');
    resetSelection();
    setQuery('');
    setShowOverQuotaOnly(false);
  };

  const brands = useMemo(() => summary?.brands ?? [], [summary]);
  const total = summary?.total;
  // 상조 view는 본인 상조뿐이라 상조명·경유 칼럼이 상수 소음 — 브랜드 1개일 때만 숨긴다
  // (한 계정이 복수 브랜드를 관리하는 예외 스코프면 구분이 필요해 그대로 노출)
  const hideBrand = readOnly && brands.length <= 1;
  const brandSummary: CouponPendingBrandSummary | undefined = useMemo(
    () => brands.find((b) => b.bugoBrand._id === selectedBrand),
    [brands, selectedBrand],
  );

  const sends = useMemo(() => sendRows ?? [], [sendRows]);
  const refunds = useMemo(() => refundRows ?? [], [refundRows]);
  // 구매수량 초과(overQuota) 발송 — 서버가 집행에서 자동 제외하는 행(§8-5 쿼터 판정).
  // 포함/expected 계산에서도 빼야 서버 재조회 대조와 일치한다. 표에는 경고 뱃지로 노출만.
  const selectableSends = useMemo(() => sends.filter((r) => !r.overQuota), [sends]);
  const overQuotaCount = sends.length - selectableSends.length;
  /**
   * 초과 발송분 도매가(발송 실비) 합 — 배너 표기용. 초과건은 대응 구매가 없어
   * 구매금액 0원 → 수수료 0원이고, 실제로 나간 돈은 도매가뿐이라 이 금액이
   * 낙전 정산에서 상조에 청구(회수)할 기준이다 ("지급 보류" 금액이 아님).
   */
  const overQuotaWholeSum = useMemo(
    () => sends.reduce((s, r) => s + (r.overQuota ? r.priceWhole ?? 0 : 0), 0),
    [sends],
  );

  // 검색·초과만보기 필터 적용 표시 목록 (포함/제외·집행 계산은 전체 sends/refunds 기준)
  const visibleSends = useMemo(
    () =>
      sends.filter(
        (r) => (!showOverQuotaOnly || r.overQuota) && matchPendingRow(r, query),
      ),
    [sends, query, showOverQuotaOnly],
  );
  const visibleRefunds = useMemo(
    () => refunds.filter((r) => matchPendingRow(r, query)),
    [refunds, query],
  );
  const visibleSelectableSends = useMemo(
    () => visibleSends.filter((r) => !r.overQuota),
    [visibleSends],
  );
  const searching = query.trim().length > 0;
  useEffect(() => {
    // 검색어·초과만보기가 바뀌면 페이지를 처음으로
    // (clamp만으로는 필터 해제 후 이전의 깊은 페이지로 되돌아가 버린다)
    setSendPage(1);
    setRefundPage(1);
  }, [query, showOverQuotaOnly]);

  // 포함분 소계 + 순액 = 로드된 행 − 초과 − 제외 (행은 이미 선택 상조 스코프)
  const includedSends = useMemo(
    () => selectableSends.filter((r) => !excl.has(r.giftCouponId)),
    [selectableSends, excl],
  );
  const includedRefunds = useMemo(
    () => refunds.filter((r) => !excl.has(r.giftCouponId)),
    [refunds, excl],
  );
  const includedSendSum = useMemo(
    () => includedSends.reduce((s, r) => s + r.amount, 0),
    [includedSends],
  );
  const includedRefundSum = useMemo(
    () => includedRefunds.reduce((s, r) => s + r.amount, 0),
    [includedRefunds],
  );
  /** 예상 정산금 — 서버 totalAmount = Σ정수 라인과 동일 기준, 추가 반올림 없음 (expectedTotalAmount로 전송) */
  const expected = includedSendSum - includedRefundSum;
  const includedCount = includedSends.length + includedRefunds.length;

  // 헤더 전체 체크 = 화면(검색 결과)의 선택 가능 행(초과 제외)에 제외 없음. 해제하면 그 전체 제외.
  // 단일 Set이라 양쪽 테이블에 같이 있는 쿠폰은 반대편 테이블에도 반영된다(쿠폰 단위 제외).
  const allSendChecked =
    visibleSelectableSends.length > 0 &&
    visibleSelectableSends.every((r) => !excl.has(r.giftCouponId));
  const allRefundChecked =
    visibleRefunds.length > 0 && visibleRefunds.every((r) => !excl.has(r.giftCouponId));
  const toggleAll = (rows: { giftCouponId: string }[], exclude: boolean) =>
    setExcl((prev) => {
      const next = new Set(prev);
      rows.forEach((r) =>
        exclude ? next.add(r.giftCouponId) : next.delete(r.giftCouponId),
      );
      return next;
    });
  const toggleAllSend = () => toggleAll(visibleSelectableSends, allSendChecked);
  const toggleAllRefund = () => toggleAll(visibleRefunds, allRefundChecked);

  // 페이지네이션 (표시용 — 검색 결과 기준)
  const activeList = tab === 'send' ? visibleSends : visibleRefunds;
  const page = tab === 'send' ? sendPage : refundPage;
  const setPage = tab === 'send' ? setSendPage : setRefundPage;
  const pageCount = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedSends = useMemo(
    () => visibleSends.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleSends, safePage],
  );
  const pagedRefunds = useMemo(
    () => visibleRefunds.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleRefunds, safePage],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  // 집행 축 토글 (확인 모달 전용) — "수익금만"/"환불만" 집행 조작. 모달을 열 때마다 둘 다 켜짐.
  const [axisSend, setAxisSend] = useState(true);
  const [axisRefund, setAxisRefund] = useState(true);

  const openSettleModal = () => {
    setAxisSend(true);
    setAxisRefund(true);
    setModalOpen(true);
  };

  // 축 토글 반영 최종 집행분 — 모달 표시와 집행 payload의 공통 기준
  const effSends = axisSend ? includedSends : [];
  const effRefunds = axisRefund ? includedRefunds : [];
  const effSendSum = axisSend ? includedSendSum : 0;
  const effRefundSum = axisRefund ? includedRefundSum : 0;
  const effExpected = effSendSum - effRefundSum;
  const effCount = effSends.length + effRefunds.length;
  /** 한쪽 축을 끈 집행은 쿠폰 단위 제외(by-filter)로 표현할 수 없다 → 축 플래그로 전송 */
  const axisSplit = !axisSend || !axisRefund;

  /** 집행 모달 확인용 — 제외된 행 목록. 운영자가 손댄 포인트라 모달에서 복원까지 지원 */
  const excludedRows = useMemo(() => {
    const rows: ExcludedPendingRow[] = [];
    sends.forEach((r) => {
      if (excl.has(r.giftCouponId))
        rows.push({
          key: `s-${r.giftCouponId}`,
          couponId: r.giftCouponId,
          kind: 'send',
          code: couponCodeLabel(r.fullCode),
          amount: r.amount,
        });
    });
    refunds.forEach((r) => {
      if (excl.has(r.giftCouponId))
        rows.push({
          key: `r-${r.giftCouponId}`,
          couponId: r.giftCouponId,
          kind: 'refund',
          code: couponCodeLabel(r.fullCode),
          amount: r.amount,
        });
    });
    return rows;
  }, [sends, refunds, excl]);

  /** 집행 성공 공통 마무리 — 토스트 · 모달 닫기 · 통계 재조회 (settle/by-filter 응답 공용) */
  const finishSettle = (result: {
    settlement: GiftCouponSettlement;
    includedSend: number;
    includedRefund: number;
    skipped?: CouponSettleSkipReport[];
  }) => {
    const skipped = result.skipped ?? [];
    // 구매수량 초과 자동 제외는 별도 문구로 — 오류가 아니라 정상 동작임을 명시 (가이드 §4)
    const overQuotaSkipped = skipped.filter((s) => s.reason === 'overQuota').length;
    const rest = skipped.filter((s) => s.reason !== 'overQuota');
    const base = `발송 ${result.includedSend}건 · 환불 ${result.includedRefund}건 집행`;
    const parts: string[] = [];
    if (rest.length > 0) {
      parts.push(describeSkipped(rest));
    }
    if (overQuotaSkipped > 0) {
      parts.push(
        `구매수량 초과 ${overQuotaSkipped}건(구매금액 0원 건)은 이번 상계에서 빠졌습니다 — 도매가는 낙전 정산에서 상계 예정`,
      );
    }
    toast.show(parts.length > 0 ? `${base}. ${parts.join(' · ')}` : `${base} 완료`);
    setModalOpen(false);
    setMemo('');
    reload(appliedRange.from, appliedRange.to);
    onSettled?.();
  };

  /** 같은 (상조,연,월) 정산서가 이미 있을 때 — 기존 Draft에 라인 추가 폴백 */
  const addToExisting = async (
    existingId: string,
    sendIds: string[],
    refundIds: string[],
  ) => {
    try {
      await patchGiftCouponSettlement(existingId, {
        addSendCouponIds: sendIds,
        addRefundCouponIds: refundIds,
      });
      toast.show('기존 정산서에 추가했습니다.');
      setModalOpen(false);
      setMemo('');
      reload(appliedRange.from, appliedRange.to);
      onSettled?.();
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '기존 정산서 추가 실패'}`);
    }
  };

  /** ID 열거 settle — 포함이 소수일 때(제외가 더 많을 때)의 경로 */
  const settleByIds = async (sendIds: string[], refundIds: string[]) => {
    try {
      const result = await settleGiftCoupon({
        target: { bugoBrand: selectedBrand },
        sendCouponIds: sendIds,
        refundCouponIds: refundIds,
        memo: memo || undefined,
      });
      finishSettle(result);
    } catch (e: any) {
      const msg: string = e?.response?.data?.message || '집행 실패';
      const existingId = extractExistingId(msg);
      if (existingId) await addToExisting(existingId, sendIds, refundIds);
      else toast.show(`오류: ${msg}`);
    }
  };

  const handleSettle = async () => {
    if (!selectedBrand || effCount === 0) return;
    const excludedIds = Array.from(excl);
    const effSendIds = effSends.map((r) => r.giftCouponId);
    const effRefundIds = effRefunds.map((r) => r.giftCouponId);
    setBusy(true);
    try {
      // 포함이 제외 이하이고 열거 한도 안이면(소수만 골라 집행) ID 열거가 더 작다 → 기존 settle.
      // ID 열거는 축 표현이 자유로워 축 분할 여부와 무관하게 쓸 수 있다.
      if (effCount <= excludedIds.length && effCount <= MAX_ENUM_IDS) {
        await settleByIds(effSendIds, effRefundIds);
        return;
      }
      try {
        const result = await settleGiftCouponByFilter({
          target: { bugoBrand: selectedBrand },
          // 기간은 조회 확정값 — 로드된 행(expected* 계산 근거)과 반드시 같은 기간
          ...ymdRangeToIso(appliedRange.from, appliedRange.to),
          excludeCouponIds: excludedIds.length > 0 ? excludedIds : undefined,
          // 축 단위 집행(수익금만/환불만) — 서버가 켠 축만 재조회해 동결 (건수 한도 없음).
          // 축 플래그 미지원 구서버는 양축을 다 세지만 expected*(끈 축 0건) 대조가
          // 어긋나 집행이 거부된다 — 잘못 집행될 일은 없고 배포 확인만 하면 됨.
          ...(axisSplit ? { includeSend: axisSend, includeRefund: axisRefund } : {}),
          expectedSendCount: effSends.length,
          expectedRefundCount: effRefunds.length,
          expectedTotalAmount: effExpected,
          memo: memo || undefined,
        });
        finishSettle(result);
      } catch (e: any) {
        const msg: string = e?.response?.data?.message || '집행 실패';
        if (msg.includes('달라졌습니다')) {
          // expected* 불일치 — 조회~집행 사이 미정산 변동(신규 발송/환불 등)으로 서버가 거부
          toast.show(msg);
          setModalOpen(false);
          reload(appliedRange.from, appliedRange.to);
          return;
        }
        const existingId = extractExistingId(msg);
        if (existingId) {
          // by-filter엔 병합 옵션이 없어 기존 Draft에 라인 추가로 폴백.
          // 단 ID 열거라 대용량이면 body 한도에 걸리므로 상한 밖에선 안내만 한다.
          if (effCount <= MAX_ENUM_IDS) {
            await addToExisting(existingId, effSendIds, effRefundIds);
          } else {
            toast.show(
              `같은 연월 정산서가 이미 있습니다 (id=${existingId}). 건수가 많아 자동 추가가 불가합니다 — 기존 Draft 삭제 후 다시 집행해 주세요.`,
            );
          }
          return;
        }
        if (e?.response?.status === 404) {
          // settle/by-filter 미배포 환경 임시 폴백 — ID 열거 settle (배포 확인 후 제거).
          // ID 열거는 body 한도가 있으므로 여기서도 상한을 지킨다 (안 지키면 413으로 재실패)
          if (effCount > MAX_ENUM_IDS) {
            toast.show(
              `이 서버는 조건 기반 집행(by-filter)이 아직 없어 ID 열거로만 집행할 수 있는데, 대상이 ${MAX_ENUM_IDS.toLocaleString(
                'ko-KR',
              )}건을 넘습니다 — 기간을 좁혀 나눠 집행해 주세요.`,
            );
            return;
          }
          await settleByIds(effSendIds, effRefundIds);
          return;
        }
        toast.show(`오류: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const rowsLoading = sendLoading || refundLoading;
  // 집행 모달 기간 표기 — 조회 확정 기간 (입력만 해둔 날짜가 아니라 실제 집행 기준)
  const rangeLabel =
    appliedRange.from || appliedRange.to
      ? `${appliedRange.from ? ymdToKr(appliedRange.from) : '처음'} ~ ${
          appliedRange.to ? ymdToKr(appliedRange.to) : '오늘'
        }`
      : '전체 기간';

  return (
    <div>
      {/* 기간 필터 — 월/단축 선택은 즉시 재조회(onRange), 직접 입력은 조회 버튼으로 */}
      <DateFilter
        label="기간(발송/환불완료일)"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFrom={setDateFrom}
        onTo={setDateTo}
        onRange={handleRange}
      >
        <button
          onClick={() => reload(dateFrom, dateTo)}
          className="rounded-lg bg-gray-700 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
        >
          조회
        </button>
      </DateFilter>

      {summaryLoading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : (
        /* ── 1단계: 통계 테이블 — 상조별 미정산 요약 (raw 미로드) ── */
        <CouponPendingSummaryTable
          brands={brands}
          total={total}
          hideBrand={hideBrand}
          onOpenDetail={openDetail}
        />
      )}

      {/* ── 2단계: 상세 모달 — 선택 상조 raw 테이블 + 검색 + 집행 ── */}
      {selectedBrand && (
        <div className="fixed inset-0 z-40 flex items-start justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeDetail} />
          <div className="relative my-4 flex max-h-[92vh] w-full max-w-[97vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            {/* 헤더 — 상조명 + summary 집계 */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
                  {hideBrand
                    ? '미정산 상세'
                    : `${brandSummary?.bugoBrand.name || ''} 미정산 상세`}
                  {!hideBrand && viaMariBadge(brandSummary?.bugoBrand.viaMari)}
                </h2>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span>
                    발송{' '}
                    <span className="font-semibold text-emerald-700">
                      +
                      {(brandSummary?.send.totalAmount ?? 0).toLocaleString('ko-KR')}
                    </span>{' '}
                    ({brandSummary?.send.count ?? 0}건)
                  </span>
                  <span>
                    환불{' '}
                    <span className="font-semibold text-rose-700">
                      −{(brandSummary?.refund.totalAmount ?? 0).toLocaleString('ko-KR')}
                    </span>{' '}
                    ({brandSummary?.refund.count ?? 0}건)
                  </span>
                  <span>
                    예상 정산금{' '}
                    <span
                      className={`font-semibold ${amountColorClass(
                        brandSummary?.netAmount ?? 0,
                      )}`}
                    >
                      {formatSettlementAmount(brandSummary?.netAmount ?? 0)}
                    </span>
                  </span>
                </p>
              </div>
              <button
                onClick={closeDetail}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="닫기"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            {rowsLoading ? (
              <div className="center-box py-24">
                <Loading />
              </div>
            ) : (
              <>
                {/* 초과 발송 경고 배너 — 초과가 있을 때만. 몰래 빠지는 게 아니라 보이고 나서 빠진다 (가이드 §3-3) */}
                {overQuotaCount > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-sm text-amber-800">
                    <span>
                      ⚠ 구매수량 초과 발송 <b>{overQuotaCount}건</b> — 구매금액 0원(수수료
                      0원) 건입니다. 도매가(발송 실비){' '}
                      <b>{overQuotaWholeSum.toLocaleString('ko-KR')}원</b>은 낙전 정산 시
                      상계 예정이라 이번 지급 정산에는 들어가지 않습니다
                    </span>
                    <button
                      onClick={() =>
                        setShowOverQuotaOnly((v) => {
                          const next = !v;
                          if (next) setTab('send'); // 초과는 발송 축에만 있음
                          return next;
                        })
                      }
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                        showOverQuotaOnly
                          ? 'border-amber-500 bg-amber-500 text-white hover:bg-amber-600'
                          : 'border-amber-300 bg-white text-amber-700 hover:bg-amber-100'
                      }`}
                    >
                      {showOverQuotaOnly ? '전체 보기' : '초과만 보기'}
                    </button>
                  </div>
                )}

                {/* 툴바 — 발송/환불 토글 + 통합 검색 */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTab('send')}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                        tab === 'send'
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      발송 수익금 · {selectableSends.length}건
                      {includedSends.length < selectableSends.length &&
                        ` (제외 ${selectableSends.length - includedSends.length})`}
                      {overQuotaCount > 0 && ` · 초과 ${overQuotaCount}`}
                    </button>
                    <button
                      onClick={() => setTab('refund')}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                        tab === 'refund'
                          ? 'border-rose-600 bg-rose-600 text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      환불 차감 · {refunds.length}건
                      {includedRefunds.length < refunds.length &&
                        ` (제외 ${refunds.length - includedRefunds.length})`}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {searching && (
                      <span className="text-xs text-gray-400">
                        검색 결과 {activeList.length}건 —{' '}
                        {readOnly ? '합계는' : '집행 대상은'} 전체 목록 기준
                      </span>
                    )}
                    <button
                      onClick={() =>
                        exportCouponPendingExcel(
                          brandSummary?.bugoBrand.name || '상조',
                          sends,
                          refunds,
                        )
                      }
                      disabled={sends.length === 0 && refunds.length === 0}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      엑셀
                    </button>
                    <CouponSearchInput value={query} onChange={setQuery} />
                  </div>
                </div>

                {/* 활성 테이블 (스크롤 영역) */}
                <div className="flex-1 overflow-auto px-5 py-4">
                  {tab === 'send' ? (
                    <CouponSendPendingTable
                      rows={pagedSends}
                      emptyMessage={
                        sends.length === 0
                          ? '정산할 발송 건이 없습니다.'
                          : '검색 결과가 없습니다.'
                      }
                      startIndex={(safePage - 1) * PAGE_SIZE}
                      readOnly={readOnly}
                      excl={excl}
                      onToggle={toggle}
                      allChecked={allSendChecked}
                      onToggleAll={toggleAllSend}
                    />
                  ) : (
                    <CouponRefundPendingTable
                      rows={pagedRefunds}
                      emptyMessage={
                        refunds.length === 0
                          ? '정산할 환불 건이 없습니다.'
                          : '검색 결과가 없습니다.'
                      }
                      startIndex={(safePage - 1) * PAGE_SIZE}
                      readOnly={readOnly}
                      excl={excl}
                      onToggle={toggle}
                      allChecked={allRefundChecked}
                      onToggleAll={toggleAllRefund}
                    />
                  )}

                  {/* 페이지네이션 */}
                  <PaginationFooter
                    page={safePage}
                    pageCount={pageCount}
                    onPage={setPage}
                  />
                </div>

                {/* 하단 고정 바 — 포함분 순액 + 집행 (검색과 무관하게 전체 기준) */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-3.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span>
                      발송{' '}
                      <span className="font-semibold text-emerald-700">
                        +{includedSendSum.toLocaleString('ko-KR')}
                      </span>{' '}
                      ({includedSends.length}건
                      {overQuotaCount > 0 && ` · 초과 ${overQuotaCount}건 제외`})
                    </span>
                    <span className="text-gray-300">−</span>
                    <span>
                      환불{' '}
                      <span className="font-semibold text-rose-700">
                        {includedRefundSum.toLocaleString('ko-KR')}
                      </span>{' '}
                      ({includedRefunds.length}건)
                    </span>
                    {excludedRows.length > 0 && (
                      <span className="text-xs text-amber-600">
                        제외 {excludedRows.length}건
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="mr-2 text-sm font-medium text-gray-500">
                        {amountLabel(expected)}
                      </span>
                      <span
                        className={`text-2xl font-bold ${
                          expected < 0 ? 'text-rose-700' : 'text-emerald-700'
                        }`}
                      >
                        {formatSettlementAmount(expected)}
                      </span>
                    </div>
                    {!readOnly && (
                      <button
                        disabled={includedCount === 0}
                        onClick={openSettleModal}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        집행 ({includedCount}건)
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 집행 확인 모달 — 집행 조작(축 토글·제외 복원)의 최종 관문 (상세 모달 위에 뜸) */}
      {modalOpen && (
        <CouponSettleConfirmModal
          brandName={brandSummary?.bugoBrand.name || ''}
          rangeLabel={rangeLabel}
          axisSend={axisSend}
          axisRefund={axisRefund}
          onToggleAxisSend={() => setAxisSend((v) => !v)}
          onToggleAxisRefund={() => setAxisRefund((v) => !v)}
          includedSendCount={includedSends.length}
          includedSendSum={includedSendSum}
          includedRefundCount={includedRefunds.length}
          includedRefundSum={includedRefundSum}
          effExpected={effExpected}
          effCount={effCount}
          overQuotaCount={overQuotaCount}
          excludedRows={excludedRows}
          onRestore={toggle}
          memo={memo}
          onMemo={setMemo}
          busy={busy}
          onConfirm={handleSettle}
          onClose={() => setModalOpen(false)}
        />
      )}

      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};
